<template>
  <div class="ticket-detail" v-loading="loading">
    <div class="back-row" @click="goBack">
      <i class="el-icon-arrow-left"></i>
      <span>Back to Service Tickets</span>
    </div>

    <template v-if="ticket">
      <div class="header">
        <div class="header-left">
          <p class="title">{{ typeLabel(ticket.ticket_type) }}</p>
          <p class="ticket-id">Ticket #{{ ticket._id && ticket._id.$id }}</p>
        </div>
        <div class="header-tags">
          <el-tag :type="paymentTag(ticket.payment_status)" size="small">
            {{ paymentLabel(ticket.payment_status) }}
          </el-tag>
          <el-tag :type="statusTag(ticket.status)" size="small">
            {{ statusLabel(ticket.status) }}
          </el-tag>
        </div>
      </div>

      <div v-if="ticket.payment_status === 'pending_payment'" class="pay-prompt">
        <div>
          <p class="pay-title">Payment required</p>
          <p class="pay-desc">
            Complete the USD {{ ticket.amount_usd }} payment to submit this
            ticket for staff review.
          </p>
        </div>
        <el-button type="primary" :loading="paying" @click="goPay">
          Pay USD {{ ticket.amount_usd }}
        </el-button>
      </div>

      <div
        v-if="ticket.status === 'pending_review' && ticket.payment_status !== 'pending_payment'"
        class="info-box info-box-neutral">
        <template v-if="ticket.payment_status === 'free'">
          Submitted. A staff member will review this request and update the
          ticket. No further action is needed on your side.
        </template>
        <template v-else>
          Payment received. A staff member will review this request and update the
          ticket. No further action is needed on your side.
        </template>
      </div>

      <div v-if="ticket.status === 'resolved'" class="info-box info-box-success">
        This ticket has been resolved. If something looks wrong, please
        open a new ticket.
      </div>

      <div v-if="ticket.status === 'rejected'" class="info-box info-box-error">
        This ticket was rejected. See staff notes below for details.
      </div>

      <div class="panel">
        <div class="panel-title">Ticket Details</div>
        <div class="kv-row">
          <div class="k">Amount</div>
          <div class="v">
            <span v-if="ticket.amount_usd > 0">USD {{ ticket.amount_usd }}</span>
            <span v-else class="free-label">Free</span>
          </div>
        </div>
        <div class="kv-row">
          <div class="k">Submitted</div>
          <div class="v">{{ formatTime(ticket.create_time) }}</div>
        </div>
        <div v-if="ticket.paid_at" class="kv-row">
          <div class="k">Paid at</div>
          <div class="v">{{ formatTime(ticket.paid_at) }}</div>
        </div>
        <div v-if="ticket.resolved_at" class="kv-row">
          <div class="k">Resolved at</div>
          <div class="v">{{ formatTime(ticket.resolved_at) }}</div>
        </div>
      </div>

      <div class="panel" v-if="ticket.ticket_type === 'results_dispute' && ticket.payload">
        <div class="panel-title">Dispute Details</div>
        <div class="kv-row" v-if="ticket.payload.dispute_type">
          <div class="k">Category</div>
          <div class="v">{{ disputeCategoryLabel(ticket.payload.dispute_type) }}</div>
        </div>
        <div class="kv-row" v-if="ticket.payload.evidence_url">
          <div class="k">Evidence</div>
          <div class="v">
            <a :href="ticket.payload.evidence_url" target="_blank" rel="noopener" class="evidence-link">
              {{ ticket.payload.evidence_url }}
            </a>
          </div>
        </div>
        <div class="reason" v-if="ticket.payload.reason">
          <div class="field-label">Reason</div>
          <div class="reason-text">{{ ticket.payload.reason }}</div>
        </div>
      </div>

      <div class="panel" v-if="ticket.ticket_type === 'name_correction' && ticket.payload">
        <div class="panel-title">Requested Change</div>
        <div class="change-grid">
          <div class="change-col">
            <div class="col-title">Current</div>
            <div class="field-row">
              <span class="field-label">Given Name (Native)</span>
              <span class="field-value">{{ ticket.payload.current_givenName || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Last Name (Native)</span>
              <span class="field-value">{{ ticket.payload.current_lastName || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Given Name (EN)</span>
              <span class="field-value">{{ ticket.payload.current_givenName_pinyin || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Last Name (EN)</span>
              <span class="field-value">{{ ticket.payload.current_lastName_pinyin || '—' }}</span>
            </div>
          </div>
          <div class="change-col">
            <div class="col-title">Requested</div>
            <div class="field-row">
              <span class="field-label">Given Name (Native)</span>
              <span class="field-value highlight">{{ ticket.payload.new_givenName || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Last Name (Native)</span>
              <span class="field-value highlight">{{ ticket.payload.new_lastName || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Given Name (EN)</span>
              <span class="field-value highlight">{{ ticket.payload.new_givenName_pinyin || '—' }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Last Name (EN)</span>
              <span class="field-value highlight">{{ ticket.payload.new_lastName_pinyin || '—' }}</span>
            </div>
          </div>
        </div>
        <div v-if="ticket.payload.reason" class="reason">
          <div class="field-label">Reason</div>
          <div class="reason-text">{{ ticket.payload.reason }}</div>
        </div>
      </div>

      <div class="panel" v-if="ticket.ticket_type === 'school_change' && ticket.payload">
        <div class="panel-title">Requested School Change</div>
        <div class="change-grid">
          <div class="change-col">
            <div class="col-title">Current</div>
            <div class="field-row">
              <span class="field-label">School</span>
              <span class="field-value">{{ ticket.payload.current_school_name || '—' }}</span>
            </div>
          </div>
          <div class="change-col">
            <div class="col-title">Requested</div>
            <div class="field-row">
              <span class="field-label">School (English)</span>
              <span class="field-value highlight">{{ ticket.payload.new_school_name_en || '—' }}</span>
            </div>
            <div class="field-row" v-if="ticket.payload.new_school_name_native">
              <span class="field-label">School (Native Language)</span>
              <span class="field-value highlight">{{ ticket.payload.new_school_name_native }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">Country / Region</span>
              <span class="field-value highlight">{{ ticket.payload.new_school_country || '—' }}</span>
            </div>
            <div class="field-row" v-if="ticket.payload.new_school_website">
              <span class="field-label">Official Website</span>
              <span class="field-value highlight">{{ ticket.payload.new_school_website }}</span>
            </div>
          </div>
        </div>
        <div v-if="ticket.payload.reason" class="reason">
          <div class="field-label">Reason</div>
          <div class="reason-text">{{ ticket.payload.reason }}</div>
        </div>
      </div>

      <div class="panel" v-if="ticket.staff_notes">
        <div class="panel-title">Staff Notes</div>
        <div class="staff-notes">{{ ticket.staff_notes }}</div>
      </div>
    </template>
  </div>
</template>

<script>
import { getServiceTicketDetail, getStripeUrlForServiceTicket, checkServiceTicketPayment } from '../../api/eae'

const TYPE_LABELS = {
  name_correction: 'Name Correction',
  school_change: 'School Change',
  results_dispute: 'Results Dispute',
}

const STATUS_LABELS = {
  pending_review: 'Pending Review',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
}

const STATUS_TAG = {
  pending_review: 'warning',
  in_progress: '',
  resolved: 'success',
  rejected: 'danger',
  cancelled: 'info',
}

const PAYMENT_LABELS = {
  pending_payment: 'Pending Payment',
  paid: 'Paid',
  free: 'Free',
}

const PAYMENT_TAG = {
  pending_payment: 'warning',
  paid: 'success',
  free: 'info',
}

const DISPUTE_CATEGORIES = {
  score: 'Score calculation',
  award: 'Award decision',
  rubric: 'Rubric application',
  other: 'Other',
}

export default {
  name: 'ServiceTicketDetail',
  data() {
    return {
      ticket: null,
      loading: false,
      paying: false,
    }
  },
  mounted() {
    this.fetchDetail()
    // If we just returned from Stripe, the callback has already flipped
    // the ticket state server-side. A quick re-fetch a moment later
    // catches any server-side latency.
    setTimeout(() => this.refreshPaymentStatusIfPending(), 1500)
  },
  methods: {
    ticketId() {
      return this.$route.params.id
    },
    fetchDetail() {
      const id = this.ticketId()
      if (!id) return
      this.loading = true
      getServiceTicketDetail(id)
        .then((res) => {
          const data = res.data
          if (data && data.code === 0) {
            this.ticket = data.data
          } else {
            this.$message.error((data && data.msg) || 'Ticket not found')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    refreshPaymentStatusIfPending() {
      if (!this.ticket || this.ticket.payment_status !== 'pending_payment') return
      checkServiceTicketPayment(this.ticketId()).then((res) => {
        const data = res.data
        if (data && data.code === 0 && data.data && data.data.payment_status !== 'pending_payment') {
          this.fetchDetail()
        }
      })
    },
    goPay() {
      this.paying = true
      getStripeUrlForServiceTicket(this.ticketId())
        .then((res) => {
          const data = res.data
          if (!data || data.code !== 0 || !data.data || !data.data.url) {
            this.$message.error((data && data.msg) || 'Failed to start payment')
            return
          }
          window.location.href = data.data.url
        })
        .finally(() => {
          this.paying = false
        })
    },
    goBack() {
      this.$router.push({ path: '/home/serviceTickets' })
    },
    typeLabel(t) {
      return TYPE_LABELS[t] || t
    },
    statusLabel(s) {
      return STATUS_LABELS[s] || s
    },
    statusTag(s) {
      return STATUS_TAG[s] !== undefined ? STATUS_TAG[s] : ''
    },
    paymentLabel(s) {
      return PAYMENT_LABELS[s] || s || '—'
    },
    paymentTag(s) {
      return PAYMENT_TAG[s] !== undefined ? PAYMENT_TAG[s] : ''
    },
    disputeCategoryLabel(c) {
      return DISPUTE_CATEGORIES[c] || c
    },
    formatTime(ts) {
      if (!ts) return ''
      const d = new Date(ts * 1000)
      const pad = (n) => (n < 10 ? '0' + n : n)
      return (
        d.getFullYear() +
        '-' +
        pad(d.getMonth() + 1) +
        '-' +
        pad(d.getDate()) +
        ' ' +
        pad(d.getHours()) +
        ':' +
        pad(d.getMinutes())
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-detail {
  padding: 24px 32px;
  max-width: 860px;
  margin: 0 auto;
}

.back-row {
  color: #ff8e3b;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 18px;

  .title {
    font-size: 22px;
    font-weight: bold;
    color: #ff8e3b;
    margin-bottom: 4px;
  }

  .ticket-id {
    color: #999;
    font-size: 12px;
  }
}

.header-tags {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
}

.free-label {
  color: #2e7d32;
  font-weight: 500;
}

.evidence-link {
  color: #ff8e3b;
  word-break: break-all;
}

.pay-prompt {
  background: #fff5f4;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 18px;

  .pay-title {
    font-weight: bold;
    color: #333;
    margin-bottom: 4px;
  }

  .pay-desc {
    color: #666;
    font-size: 13px;
  }
}

.info-box {
  padding: 14px 18px;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 18px;
  line-height: 1.5;
}

.info-box-neutral {
  background: #f0f4ff;
  color: #2b5fa8;
}

.info-box-success {
  background: #e8f7ee;
  color: #2e7d32;
}

.info-box-error {
  background: #fdecea;
  color: #b71c1c;
}

.panel {
  background: #fff;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 18px 20px;
  margin-bottom: 16px;
}

.panel-title {
  font-weight: bold;
  color: #333;
  margin-bottom: 12px;
}

.kv-row {
  display: flex;
  padding: 6px 0;
  font-size: 14px;

  .k {
    width: 140px;
    color: #888;
  }

  .v {
    flex: 1;
    color: #333;
  }
}

.change-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.change-col {
  background: #fafafa;
  border-radius: 8px;
  padding: 12px 14px;

  .col-title {
    font-size: 12px;
    color: #999;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
}

.field-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  font-size: 12px;
  color: #888;
  margin-bottom: 2px;
}

.field-value {
  font-size: 14px;
  color: #333;

  &.highlight {
    color: #ff8e3b;
    font-weight: 500;
  }
}

.reason {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px dashed #eee;
}

.reason-text {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  margin-top: 6px;
}

.staff-notes {
  font-size: 14px;
  color: #333;
  line-height: 1.6;
  white-space: pre-wrap;
}
</style>
